import { Box, Grid, Typography } from '@material-ui/core';
import { Layout, useQuery } from '@roc/feature-app-core';
import { makeStyles } from '@material-ui/core/styles';
import {
  DialogState,
  EditableDialog,
  StandardDialog,
  Button,
  DataGrid,
  SelectMode,
  createRadioField,
  TextField,
} from '@roc/ui';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useInternalSecondAppraisalStore } from '../hooks';
import { Alert, AlertTitle } from '@material-ui/lab';
import { Check, CheckBox, CheckBoxOutlineBlank } from '@material-ui/icons';

const useStyles = makeStyles({
  instruction: {
    fontWeight: 600,
  },
  paymentDetails: {
    fontWeight: 800,
    marginTop: '20px',
    fontStyle: 'italic',
  },
  tabsContainer: {
    border: 'none !important',
    marginTop: '-35px',
  },
  container: {
    paddingTop: 12,
  },
  closeButtonContainer: {
    paddingTop: 16,
  },
  button: {
    width: '100%',
  },
});

const CheckboxListener = observer(({ params, propertyId }) => {
  const { internalSecondAppraisalStore } = useInternalSecondAppraisalStore();
  const { selectedPropertyIds } = internalSecondAppraisalStore;
  const selected = selectedPropertyIds.includes(propertyId);

  params.node.setSelected(selected);

  return <></>;
});

export const SecondAppraisalForm = observer(() => {
  const { loanId } = useParams<{ loanId: string }>();
  const classes = useStyles();

  const { internalSecondAppraisalStore } = useInternalSecondAppraisalStore();
  const formStore = internalSecondAppraisalStore.secondAppraisalForm;

  const columns = [
    {
      field: 'fullAddress',
      headerName: 'Property Address',
      cellRenderer: 'addressCellRenderer',
    },
  ];

  function onRowSelected(params) {
    setRowIds(params);
  }

  function setRowIds(params) {
    internalSecondAppraisalStore.setPropertyIds(
      params?.data.propertyId,
      params?.node.selected
    );
  }

  const frameworkComponents = {
    addressCellRenderer(params) {
      const data = params.node?.data;
      return data ? (
        <>
          {data.fullAddress}
          <CheckboxListener params={params} propertyId={data.propertyId} />
        </>
      ) : null;
    },
  };

  return (
    <Grid container spacing={2}>
      <Grid item container xs={12}>
        <Typography className={classes.instruction}>
          Choose Properties
        </Typography>
      </Grid>
      <Grid item container xs={12}>
        <DataGrid
          selectMode={SelectMode.MULTIPLE}
          columns={columns}
          onRowSelected={e => onRowSelected(e)}
          frameworkComponents={frameworkComponents}
          rows={internalSecondAppraisalStore.loanProperties}
          showFilters={false}
          domLayout="autoHeight"
          suppressRowClickSelection={true}
        />
      </Grid>
    </Grid>
  );
});

export const SecondAppraisalError = observer(() => {
  return (
    <Box pb={4}>
      <Alert severity="error">
        <AlertTitle>Error</AlertTitle>
        <p>The first appraisal does not seem to be in. Please check.</p>
        <p>If you feel this is an issue, reach out to the Technology Team.</p>
      </Alert>
    </Box>
  );
});

export const SecondAppraisalSuccess = observer(() => {
  const { loanId } = useParams<{ loanId: string }>();
  const { internalSecondAppraisalStore } = useInternalSecondAppraisalStore();
  const {
    loanPropertiesSelectedNonPaid,
    formLink,
  } = internalSecondAppraisalStore;
  const addresses = loanPropertiesSelectedNonPaid
    .map(property => property.fullAddress);
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box pb={2}>
          <Alert severity="success">
            <AlertTitle>Success</AlertTitle>
            <Box pb={1}>
              Your link was generated successfully for the following properties:
            </Box>
            <Box pl={2}>
              <ul>
                {addresses.map(address => (
                  <li>{address}</li>
                ))}
              </ul>
            </Box>
          </Alert>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <TextField
          testId="link"
          variant="outlined"
          label="Link"
          value={formLink}
          fullWidth
        />
      </Grid>
      <Grid item xs={12}>
        <Box display="flex" justifyContent="center">
          <Grid item>
            <Button
              onClick={() => {
                internalSecondAppraisalStore.copyLink();
              }}
              variant="contained"
              color="primary"
              testId="close"
              style={{ marginRight: '16px' }}
            >
              Copy Link
            </Button>
          </Grid>
          {/* <Grid item>
            <Button
              onClick={() => {
                internalSecondAppraisalStore.sendLink(loanId);
              }}
              variant="contained"
              color="primary"
              testId="close"
            >
              Send to underwriting chatroom
            </Button>
          </Grid> */}
        </Box>
      </Grid>
    </Grid>
  );
});

export const SecondAppraisalPaidInAdvance = observer(() => {
  const { internalSecondAppraisalStore } = useInternalSecondAppraisalStore();
  const {
    allLoanProperties,
    alreadyPaidProperties,
  } = internalSecondAppraisalStore;

  const addresses = allLoanProperties
    .filter(property => alreadyPaidProperties.includes(property.propertyId))
    .map(property => property.fullAddress);

  return (
    <Alert severity="info">
      <AlertTitle>Paid in advance</AlertTitle>
      <ul>
        {addresses.map(address => (
          <li>{address}</li>
        ))}
      </ul>
    </Alert>
  );
});

export const SecondAppraisalLinkSuccessAlreadyPaid = observer(() => {
  return (
    <Box pb={4}>
      <Alert severity="success">
        <AlertTitle>Success</AlertTitle>
        <p>Your order(s) has been placed successfully.</p>
      </Alert>
    </Box>
  );
});
